/**
 * Power and Current calculation utilities for EV charging
 */

// Standard voltage levels for different regions and connector types
export const VOLTAGE_LEVELS = {
  // AC Single Phase
  AC_SINGLE_PHASE_230V: 230,  // Europe, Asia
  AC_SINGLE_PHASE_240V: 240,  // North America
  AC_SINGLE_PHASE_208V: 208,  // North America commercial

  // AC Three Phase
  AC_THREE_PHASE_400V: 400,   // Europe, Asia (line-to-line)
  AC_THREE_PHASE_480V: 480,   // North America (line-to-line)
  AC_THREE_PHASE_208V: 208,   // North America (line-to-line)

  // DC Fast Charging
  DC_400V: 400,               // Typical DC fast charging
  DC_800V: 800,               // High-power DC charging
  DC_1000V: 1000,             // Ultra-fast DC charging
};

// Connector type to typical voltage mapping
export const CONNECTOR_VOLTAGES: Record<string, { voltage: number; phases: number }> = {
  'Type2': { voltage: 400, phases: 3 },      // European AC, typically 3-phase
  'Type1': { voltage: 240, phases: 1 },      // North American/Japanese AC, single-phase
  'J1772': { voltage: 240, phases: 1 },      // North American AC, single-phase
  'CCS': { voltage: 400, phases: 0 },        // DC charging (phases = 0 for DC)
  'CCS1': { voltage: 400, phases: 0 },       // North American DC
  'CCS2': { voltage: 400, phases: 0 },       // European DC
  'CHAdeMO': { voltage: 400, phases: 0 },    // Japanese DC
  'GB/T': { voltage: 400, phases: 3 },       // Chinese, can be AC or DC
  'GBT': { voltage: 400, phases: 3 },        // Alternative spelling
  'Tesla': { voltage: 400, phases: 0 },      // Tesla Supercharger (DC)
};

/**
 * Calculate current from power based on voltage and phases
 * @param power Power in kW
 * @param voltage Voltage in V
 * @param phases Number of phases (1 for single-phase, 3 for three-phase, 0 for DC)
 * @returns Current in Amperes
 */
export function calculateCurrent(power: number, voltage: number, phases: number): number {
  if (!power || !voltage) return 0;

  // Convert kW to W
  const powerInWatts = power * 1000;

  if (phases === 3) {
    // Three-phase AC: I = P / (V × √3)
    return Math.round(powerInWatts / (voltage * Math.sqrt(3)) * 10) / 10;
  } else {
    // Single-phase AC or DC: I = P / V
    return Math.round(powerInWatts / voltage * 10) / 10;
  }
}

/**
 * Calculate power from current based on voltage and phases
 * @param current Current in Amperes
 * @param voltage Voltage in V
 * @param phases Number of phases (1 for single-phase, 3 for three-phase, 0 for DC)
 * @returns Power in kW
 */
export function calculatePower(current: number, voltage: number, phases: number): number {
  if (!current || !voltage) return 0;

  let powerInWatts: number;

  if (phases === 3) {
    // Three-phase AC: P = V × I × √3
    powerInWatts = voltage * current * Math.sqrt(3);
  } else {
    // Single-phase AC or DC: P = V × I
    powerInWatts = voltage * current;
  }

  // Convert W to kW and round to 1 decimal place
  return Math.round(powerInWatts / 100) / 10;
}

/**
 * Get suggested current based on power and connector type
 * @param power Power in kW
 * @param connectorType Type of connector
 * @returns Suggested current in Amperes
 */
export function getSuggestedCurrent(power: number, connectorType: string): number {
  const connectorInfo = CONNECTOR_VOLTAGES[connectorType];

  if (!connectorInfo) {
    // Default to 3-phase 400V if connector type is unknown
    return calculateCurrent(power, 400, 3);
  }

  return calculateCurrent(power, connectorInfo.voltage, connectorInfo.phases);
}

/**
 * Determine if connector type is DC
 * @param connectorType Type of connector
 * @returns true if DC connector
 */
export function isDCConnector(connectorType: string): boolean {
  const dcConnectors = ['CCS', 'CCS1', 'CCS2', 'CHAdeMO', 'Tesla'];
  return dcConnectors.includes(connectorType);
}

/**
 * Get voltage configuration for connector type
 * @param connectorType Type of connector
 * @returns Voltage configuration
 */
export function getVoltageConfig(connectorType: string): { voltage: number; phases: number; isDC: boolean } {
  const config = CONNECTOR_VOLTAGES[connectorType] || { voltage: 400, phases: 3 };
  return {
    ...config,
    isDC: config.phases === 0
  };
}

/**
 * Format current value for display
 * @param current Current in Amperes
 * @returns Formatted string
 */
export function formatCurrent(current: number): string {
  if (!current) return '0 A';
  return `${current.toFixed(1)} A`;
}

/**
 * Format power value for display
 * @param power Power in kW
 * @returns Formatted string
 */
export function formatPower(power: number): string {
  if (!power) return '0 kW';
  return `${power.toFixed(1)} kW`;
}